import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { formatCHF, ORDER_STATUS_LABELS } from "@/lib/orderStatus";
import { SkeletonCard } from "@/components/Skeleton";
import { downloadCsv } from "@/lib/csvExport";
import { FileDown, TrendingUp, ShoppingBag, Users, ChartBar, Star, AlertTriangle } from "lucide-react";

const MONTHS_FR = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function currentYearMonth() {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function buildYearOptions() {
    const now = new Date().getFullYear();
    return [now - 2, now - 1, now, now + 1];
}

function KpiTile({ title, icon: Icon, value, hint, testId, accent }) {
    return (
        <div className={`bc-kpi-card ${accent ? "bc-kpi-card--accent" : ""}`} data-testid={testId}>
            <div className="bc-kpi-card__head">
                <span className="bc-kpi-card__title">{title}</span>
                {Icon && <Icon size={16} className="bc-kpi-card__icon" />}
            </div>
            <div className="bc-kpi-card__body">
                <p className="bc-kpi-card__big">{value}</p>
                {hint && <p className="bc-kpi-card__hint">{hint}</p>}
            </div>
        </div>
    );
}

function escapeCsv(v) {
    if (v == null) return "";
    const s = String(v);
    if (s.includes(";") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function toRows(sections) {
    const lines = [];
    sections.forEach((sec) => {
        if (sec.title) lines.push([sec.title]);
        if (sec.headers) lines.push(sec.headers);
        sec.rows.forEach((r) => lines.push(r));
        lines.push([]);
    });
    return "\uFEFF" + lines.map((row) => row.map(escapeCsv).join(";")).join("\r\n");
}

export default function AdminAudit() {
    const initial = currentYearMonth();
    const [year, setYear] = useState(initial.year);
    const [month, setMonth] = useState(initial.month);

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ["admin-audit", year, month],
        queryFn: async () => (await api.get(`/admin/orders/summary?year=${year}&month=${month}`)).data,
        keepPreviousData: true,
    });

    const setPrev = () => {
        let y = year, m = month - 1;
        if (m < 1) { m = 12; y -= 1; }
        setMonth(m); setYear(y);
    };
    const setNext = () => {
        let y = year, m = month + 1;
        if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y);
    };
    const setThisMonth = () => {
        const { year: y, month: mm } = currentYearMonth();
        setYear(y); setMonth(mm);
    };

    // Sparkline max for daily bars
    const maxDaily = useMemo(() => {
        if (!data?.daily_revenue?.length) return 1;
        return data.daily_revenue.reduce((m, x) => Math.max(m, x.revenue || 0), 0) || 1;
    }, [data]);

    const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month]);
    const dailyMap = useMemo(() => {
        const m = {};
        (data?.daily_revenue || []).forEach((d) => { m[d.day] = d; });
        return m;
    }, [data]);

    const handleExport = () => {
        if (!data) return;
        const label = data.period.label.replace(/\s+/g, "-").toLowerCase();
        const totals = data.totals || {};
        const revLabel = ORDER_STATUS_LABELS;
        const statusRows = Object.entries(data.counts_by_status || {}).map(
            ([k, v]) => [revLabel[k] || k, v]
        );

        const sections = [
            {
                title: [`Audit mensuel — ${data.period.label}`],
                headers: null,
                rows: [
                    ["Période", `${data.period.start.slice(0, 10)} → ${data.period.end.slice(0, 10)}`],
                    ["Total commandes", totals.orders ?? 0],
                    ["Commandes actives", totals.active ?? 0],
                    ["Commandes annulées", totals.cancelled ?? 0],
                    ["Chiffre d'affaires HT", (totals.revenue_ht ?? 0).toFixed(2)],
                    ["Panier moyen HT", (totals.avg_order_value ?? 0).toFixed(2)],
                    ["Nombre d'avis", data.reviews?.count ?? 0],
                    ["Note moyenne (/5)", data.reviews?.avg_rating ?? 0],
                    ["Réclamations", data.reviews?.complaints ?? 0],
                ],
            },
            {
                title: ["Répartition par statut"],
                headers: ["Statut", "Nombre"],
                rows: statusRows,
            },
            {
                title: ["Top 10 articles (par CA)"],
                headers: ["Code", "Article", "Quantité (kg)", "CA HT (CHF)"],
                rows: (data.top_articles || []).map((a) => [
                    a.code, a.article, a.quantity.toFixed(2), a.revenue.toFixed(2),
                ]),
            },
            {
                title: ["Top 10 clients (par CA)"],
                headers: ["Raison sociale", "E-mail", "Commandes", "CA HT (CHF)"],
                rows: (data.top_clients || []).map((c) => [
                    c.raison_sociale, c.email, c.orders, c.revenue.toFixed(2),
                ]),
            },
            {
                title: ["CA quotidien"],
                headers: ["Jour", "Commandes", "CA HT (CHF)"],
                rows: (data.daily_revenue || []).map((d) => [
                    `${String(d.day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`,
                    d.orders, d.revenue.toFixed(2),
                ]),
            },
        ];
        downloadCsv(`boucherie-costa-audit-${label}.csv`, toRows(sections));
    };

    if (isLoading && !data) {
        return (
            <div data-testid="admin-audit-loading" className="grid gap-4 md:grid-cols-3">
                <SkeletonCard lines={3} />
                <SkeletonCard lines={3} />
                <SkeletonCard lines={3} />
            </div>
        );
    }

    const d = data || {};
    const totals = d.totals || {};
    const reviews = d.reviews || {};

    return (
        <div className="bc-anim-in" data-testid="admin-audit">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
                <div>
                    <h2 className="font-serif text-2xl text-[#D4BC7C]">Audit mensuel</h2>
                    <p className="text-xs uppercase tracking-widest text-[#7a7a7a] mt-1" data-testid="audit-period-label">
                        {d.period?.label || "—"}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={!data}
                    className="flex items-center gap-2 text-xs uppercase tracking-widest px-4 py-2 border border-[#D4BC7C] text-[#D4BC7C] hover:bg-[#D4BC7C] hover:text-black transition-colors disabled:opacity-40"
                    data-testid="audit-export-btn"
                >
                    <FileDown size={14} />
                    Exporter CSV
                </button>
            </div>

            {/* Period selector */}
            <div className="flex flex-wrap items-center gap-3 mb-8 pb-6 border-b" style={{ borderColor: "var(--bc-border)" }}>
                <button
                    type="button"
                    onClick={setPrev}
                    className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#F2ECE0]/20 hover:border-[#D4BC7C] hover:text-[#D4BC7C] transition-colors"
                    data-testid="audit-prev-month"
                >
                    ← Mois préc.
                </button>
                <select
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    className="bg-black border border-[#F2ECE0]/20 text-[#F2ECE0] px-3 py-1.5 text-sm focus:border-[#D4BC7C] focus:outline-none"
                    data-testid="audit-month-select"
                >
                    {MONTHS_FR.map((m, idx) => (
                        <option key={idx} value={idx + 1}>{m}</option>
                    ))}
                </select>
                <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="bg-black border border-[#F2ECE0]/20 text-[#F2ECE0] px-3 py-1.5 text-sm focus:border-[#D4BC7C] focus:outline-none"
                    data-testid="audit-year-select"
                >
                    {buildYearOptions().map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={setNext}
                    className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#F2ECE0]/20 hover:border-[#D4BC7C] hover:text-[#D4BC7C] transition-colors"
                    data-testid="audit-next-month"
                >
                    Mois suiv. →
                </button>
                <button
                    type="button"
                    onClick={setThisMonth}
                    className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#D4BC7C]/40 text-[#D4BC7C] hover:border-[#D4BC7C] transition-colors"
                    data-testid="audit-this-month"
                >
                    Mois en cours
                </button>
                {isFetching && (
                    <span className="text-[10px] text-[#7a7a7a] tracking-widest uppercase" data-testid="audit-loading-inline">
                        Chargement…
                    </span>
                )}
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <KpiTile
                    title="CA HT"
                    icon={TrendingUp}
                    value={formatCHF(totals.revenue_ht || 0)}
                    hint="hors commandes annulées"
                    testId="audit-kpi-revenue"
                    accent
                />
                <KpiTile
                    title="Commandes actives"
                    icon={ShoppingBag}
                    value={String(totals.active || 0)}
                    hint={`${totals.cancelled || 0} annulée${(totals.cancelled || 0) > 1 ? "s" : ""}`}
                    testId="audit-kpi-orders"
                />
                <KpiTile
                    title="Panier moyen"
                    icon={ChartBar}
                    value={formatCHF(totals.avg_order_value || 0)}
                    hint="HT / commande active"
                    testId="audit-kpi-avg"
                />
                <KpiTile
                    title="Note moyenne"
                    icon={Star}
                    value={reviews.count ? `${reviews.avg_rating.toFixed(1)} / 5` : "—"}
                    hint={reviews.count ? `${reviews.count} avis · ${reviews.complaints} réclamation${reviews.complaints > 1 ? "s" : ""}` : "aucun avis"}
                    testId="audit-kpi-rating"
                />
            </div>

            {/* Daily revenue bar chart */}
            <div className="bc-kpi-card mb-6" data-testid="audit-daily-chart">
                <div className="bc-kpi-card__head">
                    <span className="bc-kpi-card__title">CA quotidien</span>
                    <ChartBar size={16} className="bc-kpi-card__icon" />
                </div>
                {(!d.daily_revenue || d.daily_revenue.length === 0) ? (
                    <p className="text-sm text-[#A0A0A0] mt-4">Aucune commande facturée sur cette période.</p>
                ) : (
                    <div className="mt-6">
                        <div className="flex items-end gap-[3px] h-28">
                            {Array.from({ length: daysInMonth }, (_, i) => {
                                const day = i + 1;
                                const entry = dailyMap[day];
                                const rev = entry?.revenue || 0;
                                const heightPct = rev > 0 ? Math.max(4, (rev / maxDaily) * 100) : 0;
                                return (
                                    <div
                                        key={day}
                                        className="flex-1 relative group"
                                        title={entry ? `${day}: ${formatCHF(rev)} · ${entry.orders} cmd` : `${day}: —`}
                                    >
                                        <div
                                            className="w-full transition-colors"
                                            style={{
                                                height: `${heightPct}%`,
                                                background: rev > 0 ? "#D4BC7C" : "rgba(242,236,224,0.08)",
                                                minHeight: rev > 0 ? "3px" : "3px",
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-between mt-2 text-[10px] text-[#7a7a7a]">
                            <span>1</span>
                            <span>{Math.round(daysInMonth / 2)}</span>
                            <span>{daysInMonth}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Top articles */}
                <div className="bc-kpi-card" data-testid="audit-top-articles">
                    <div className="bc-kpi-card__head">
                        <span className="bc-kpi-card__title">Top articles</span>
                        <TrendingUp size={16} className="bc-kpi-card__icon" />
                    </div>
                    {(d.top_articles || []).length === 0 ? (
                        <p className="text-sm text-[#A0A0A0] mt-4">Aucun article vendu.</p>
                    ) : (
                        <table className="w-full mt-4 text-xs">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-widest text-[#7a7a7a] border-b" style={{ borderColor: "var(--bc-border)" }}>
                                    <th className="text-left py-2 pr-2">Article</th>
                                    <th className="text-right py-2 px-2">Qté</th>
                                    <th className="text-right py-2 pl-2">CA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(d.top_articles || []).map((a, idx) => (
                                    <tr key={idx} className="border-b" style={{ borderColor: "rgba(242,236,224,0.06)" }} data-testid={`audit-article-${idx}`}>
                                        <td className="py-2 pr-2">
                                            <span className="block text-[#F2ECE0]">{a.article}</span>
                                            <span className="text-[10px] text-[#7a7a7a]">{a.code}</span>
                                        </td>
                                        <td className="py-2 px-2 text-right text-[#F2ECE0]/80">{a.quantity.toFixed(2)} kg</td>
                                        <td className="py-2 pl-2 text-right text-[#D4BC7C] font-medium">{formatCHF(a.revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Top clients */}
                <div className="bc-kpi-card" data-testid="audit-top-clients">
                    <div className="bc-kpi-card__head">
                        <span className="bc-kpi-card__title">Top clients</span>
                        <Users size={16} className="bc-kpi-card__icon" />
                    </div>
                    {(d.top_clients || []).length === 0 ? (
                        <p className="text-sm text-[#A0A0A0] mt-4">Aucun client facturé.</p>
                    ) : (
                        <table className="w-full mt-4 text-xs">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-widest text-[#7a7a7a] border-b" style={{ borderColor: "var(--bc-border)" }}>
                                    <th className="text-left py-2 pr-2">Client</th>
                                    <th className="text-right py-2 px-2">Cmd</th>
                                    <th className="text-right py-2 pl-2">CA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(d.top_clients || []).map((c, idx) => (
                                    <tr key={c.user_id || idx} className="border-b" style={{ borderColor: "rgba(242,236,224,0.06)" }} data-testid={`audit-client-${idx}`}>
                                        <td className="py-2 pr-2">
                                            <span className="block text-[#F2ECE0]">{c.raison_sociale}</span>
                                            <span className="text-[10px] text-[#7a7a7a] truncate block max-w-[220px]">{c.email}</span>
                                        </td>
                                        <td className="py-2 px-2 text-right text-[#F2ECE0]/80">{c.orders}</td>
                                        <td className="py-2 pl-2 text-right text-[#D4BC7C] font-medium">{formatCHF(c.revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Status breakdown */}
            <div className="bc-kpi-card" data-testid="audit-status-breakdown">
                <div className="bc-kpi-card__head">
                    <span className="bc-kpi-card__title">Répartition par statut</span>
                    <AlertTriangle size={16} className="bc-kpi-card__icon" />
                </div>
                {Object.keys(d.counts_by_status || {}).length === 0 ? (
                    <p className="text-sm text-[#A0A0A0] mt-4">Aucune commande sur cette période.</p>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                        {Object.entries(d.counts_by_status || {}).map(([status, count]) => (
                            <div key={status} className="border p-3" style={{ borderColor: "var(--bc-border)" }} data-testid={`audit-status-${status}`}>
                                <p className="text-[10px] uppercase tracking-widest text-[#7a7a7a]">
                                    {ORDER_STATUS_LABELS[status] || status}
                                </p>
                                <p className="font-serif text-2xl text-[#D4BC7C] mt-1">{count}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
